import React, { FC, useEffect, useState } from "react";
import CustomTable from "../../../../components/Tables/CustomTable";
import { MoreVertical } from "lucide-react";
import DropdownMenu from "../../../ui/DropdownMenu";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../redux/app/hooks";
import {
  fetchAllDisputeOrders,
  setSelectedDispute,
} from "../../../../redux/features/orderSlice";
import { RootState } from "../../../../redux/app/store";
import moment from "moment";
import { cleanObject, formatNumberWithCommas } from "../../../../helpers";
import { getStatusStyles } from "../util";
import ClickableUserName from "../../../../components/shared/ClickableUserName";

interface OrderTableProps {
  search: string;
  startDate: Date | null;
  endDate: Date | null;
}

const DisputeOrderTable: FC<OrderTableProps> = ({
  search,
  startDate,
  endDate,
}) => {
  const { loading, disputeOrders } = useAppSelector(
    (state: RootState) => state.orders,
  );
  const [disputePageLimit, setDisputePageLimit] = useState<number>(10);
  const [disputePage, setDisputePage] = useState<number>(0);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const disputedORderColumns = [
    { label: "S/N", field: "id" },
    { label: "Contract", field: "contract_code" },
    { label: "Order Weight(Kg)", field: "dispute_weight" },
    { label: "Name of Vendor", field: "vendor_name" },
    { label: "Name of Buyer", field: "buyer_name" },
    // { label: "Pickup Location", field: "pickup_location" },
    { label: "Order Amount", field: "order_amount" },
    { label: "Created Date", field: "created_date" },
    { label: "Dispatch Status", field: "dispatch_status" },
    { label: "Order Status", field: "status" },
    { label: "Dispute Status", field: "dispute_status" },
    { label: "Buyer's reason for dispute", field: "dispute_reason" },
    { label: "Action", field: "dispute_action" },
  ];

  const disputeDropdownOptions = [
    {
      label: "View order details",
      onClick: (item: any) => {
        navigate(`/dispute-order-details/${item?.id}`);
        dispatch(setSelectedDispute(item));
      },
      returnAll: true,
    },
    // {
    //   label: <div className="text-accent">Approve dispute order</div>,
    //   onClick: () => setOpenApproveDisputeModal(true),
    // },
  ];

  const disputeOrderData = disputeOrders?.data?.map(
    (dispute: any, index: number) => {
      const isPaid =
        dispute?.status === "PAID" || dispute?.status === "COMPLETED";
      return {
        _disputeId: dispute?.id,
        id: index + 1,
        contract_id: dispute?.contract?.title,
        dispute_weight: dispute?.order?.contract?.actualWeight ?? "N/A",
        vendor_name: (
          <ClickableUserName
            user={dispute?.order?.contract?.vendorId}
            userType="VENDOR"
          />
        ),
        buyer_name: (
          <ClickableUserName
            user={dispute?.order?.contract?.buyerId}
            userType="BUYER"
          />
        ),
        pickup_location: dispute?.pickupLocation?.address,
        contract_code: dispute?.order?.contract?.contractCode,
        created_date: moment(dispute?.createdAt).format("Do MMM YYYY"),
        order_amount: dispute?.order?.orderAmount
          ? `₦ ${formatNumberWithCommas(dispute?.order?.orderAmount)}`
          : "N/A",
        dispute_reason: dispute?.question?.disputeReason?.name,
        dispatch_status: (
          <span
            className={`text-xs px-3 py-1 rounded-md font-semibold ${getStatusStyles(
              dispute?.order?.dispatchStatus,
            )}`}
          >
            {dispute?.order?.dispatchStatus}
          </span>
        ),
        status: (
          <span
            className={`text-xs px-3 py-1 rounded-md font-semibold ${
              isPaid
                ? "bg-green-50 text-green-500 border border-green-500"
                : dispute?.order?.status === "PENDING"
                  ? "bg-yellow-50 text-yellow-500 border border-yellow-500"
                  : "bg-red-50 text-red-500 border border-red-500"
            }`}
          >
            {dispute?.order?.status}
          </span>
        ),
        dispute_status: (
          <span
            className={`text-xs px-3 py-1 rounded-md font-semibold ${
              dispute?.disputeStatus === "RESOLVED"
                ? "bg-green-50 text-green-500 border border-green-500"
                : dispute?.disputeStatus === "UNDER_REVIEW"
                  ? "bg-indigo-50 text-indigo-500 border border-indigo-500"
                  : "bg-red-50 text-red-500 border border-red-500"
            }`}
          >
            {dispute?.disputeStatus}
          </span>
        ),
        dispute_action: (
          <DropdownMenu
            options={disputeDropdownOptions}
            icon={<MoreVertical style={{ cursor: "pointer" }} />}
            item={dispute}
          />
        ),
      };
    },
  );

  useEffect(() => {
    const params = cleanObject({
      limit: disputePageLimit,
      page: disputePage + 1,
      filter: search,
      startDate,
      endDate,
    });
    dispatch(fetchAllDisputeOrders(params));
  }, [dispatch, disputePage, disputePageLimit, search, startDate, endDate]);

  return (
    <CustomTable
      data={{
        columns: disputedORderColumns,
        rows: disputeOrderData,
      }}
      onRowClick={(row) => {
        const dispute = disputeOrders?.data?.find(
          (d: any) => d?.id === row._disputeId,
        );
        if (dispute) {
          navigate(`/dispute-order-details/${dispute.id}`);
          dispatch(setSelectedDispute(dispute));
        }
      }}
      noRecordText="No Disputed Order Available"
      loading={loading}
      showFooter={true}
      totalPages={disputeOrders?.data?.totalPages}
      itemsPerPage={disputeOrders?.data?.itemsPerPage}
      currentPage={disputeOrders?.data?.currentPage}
      onPageChange={(value: number) => setDisputePage(value)}
      onLimitChange={(value: number) => setDisputePageLimit(value)}
    />
  );
};

export default DisputeOrderTable;
